import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Stack, Chip, Button, IconButton,
  CircularProgress, Paper, Divider,
} from '@mui/material';
import {
  AutoAwesome, Close as CloseIcon, CheckCircle, Cancel,
  Refresh, Psychology, MonitorHeart, MedicalServices,
  Healing, Assignment, NoteAlt, EventNote,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  generateNurseNote,
  loadPatientDiagnosisdb,
  loadVitalsHistorydb,
  loadChartedMedicationsdb,
  loadWardRoundsdb,
} from '../BaseURL';

const AI_GRADIENT = 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)';

/**
 * AI-generated nurse discharge note. Self-contained: when the dialog opens
 * it fetches the patient's clinical context (diagnosis, vitals, medications,
 * ward rounds) and shows what's available before generation. The result
 * targets a short (≤4-line) discharge note.
 *
 * Props:
 *  - open, onClose
 *  - patient            { id, fullName, age, gender, visitDate, dischargeReason }
 *                       fullName/visitDate may also arrive as `name`/`dat`
 *                       (matches the pending-patient row shape).
 *  - dischargeReason    string — currently selected reason in the form
 *  - onGenerated(text)  populates the parent's discharge-notes textarea
 */
export default function AIDischargeNoteDialog({
  open, onClose, patient, dischargeReason = '', onGenerated,
}) {
  const [genIndex, setGenIndex]       = useState(0);
  const [generating, setGenerating]   = useState(false);
  const [generated, setGenerated]     = useState('');
  const [loadingCtx, setLoadingCtx]   = useState(false);
  const [ctx, setCtx] = useState({
    complaint: '', history: '', exam: '', other: '',
    documenting: '', procedure_: '', admmission_note: '',
    vitals: [], medications: [], rounds: [],
  });

  // Patient identity normalised across the two shapes (pending-list vs InPatient)
  const pid     = patient?.id;
  const pname   = patient?.fullName || patient?.name || '';
  const visitDt = patient?.visitDate || patient?.dat || '';

  useEffect(() => {
    if (!open) return;
    setGenIndex(0);
    setGenerated('');
    setGenerating(false);
    if (!pid) return;

    setLoadingCtx(true);
    Promise.allSettled([
      axios.get(loadPatientDiagnosisdb,         { params: { patientId: pid, dat: visitDt } }),
      axios.get(loadVitalsHistorydb,            { params: { patientId: pid, visitDate: visitDt } }),
      axios.get(loadChartedMedicationsdb,       { params: { patientId: pid, startDate: visitDt || '1900-01-01' } }),
      axios.get(loadWardRoundsdb,               { params: { patientId: pid, startDate: visitDt } }),
    ])
      .then(([dx, vt, md, wr]) => {
        const d = dx.status === 'fulfilled' && dx.value.data?.success ? (dx.value.data.data || {}) : {};
        const v = vt.status === 'fulfilled' && vt.value.data?.success ? (vt.value.data.results || vt.value.data.history || []) : [];
        const m = md.status === 'fulfilled' && md.value.data?.success ? (md.value.data.charts || []) : [];
        const r = wr.status === 'fulfilled' && wr.value.data?.success ? (wr.value.data.data || []) : [];
        setCtx({
          complaint:        d.complaint     || '',
          history:          d.history       || '',
          exam:             d.exam          || '',
          other:            d.other         || '',
          documenting:      d.documenting   || '',
          procedure_:       d.procedure_    || '',
          admmission_note:  d.admmission_note || '',
          vitals:      Array.isArray(v) ? v : [],
          medications: Array.isArray(m) ? m : [],
          rounds:      Array.isArray(r) ? r : [],
        });
      })
      .finally(() => setLoadingCtx(false));
  }, [open, pid, visitDt]);

  const latestVitals = ctx.vitals?.[0] || null;

  const params = useMemo(() => ([
    {
      key: 'reason', icon: <EventNote fontSize="small" />, label: 'Discharge reason',
      present: !!dischargeReason,
      preview: dischargeReason || 'Not selected',
    },
    {
      key: 'diagnosis', icon: <Psychology fontSize="small" />, label: 'Final diagnosis',
      present: !!ctx.documenting,
      preview: ctx.documenting || 'Not recorded',
    },
    {
      key: 'procedure', icon: <Healing fontSize="small" />, label: 'Procedure',
      present: !!ctx.procedure_,
      preview: ctx.procedure_ || 'None',
    },
    {
      key: 'plan', icon: <Assignment fontSize="small" />, label: 'Doctor\u2019s notes',
      present: !!(ctx.other || ctx.history || ctx.complaint || ctx.admmission_note),
      preview: (ctx.other || ctx.admmission_note || ctx.history || ctx.complaint || '').slice(0, 90) || 'Not recorded',
    },
    {
      key: 'vitals', icon: <MonitorHeart fontSize="small" />, label: 'Latest vitals',
      present: !!latestVitals,
      preview: latestVitals
        ? [
            latestVitals.tem  ? `T ${latestVitals.tem}\u00B0C` : null,
            latestVitals.pulse? `P ${latestVitals.pulse}`      : null,
            latestVitals.pre  ? `BP ${latestVitals.pre}`       : null,
            latestVitals.res  ? `RR ${latestVitals.res}`       : null,
            latestVitals.spo2 ? `SpO\u2082 ${latestVitals.spo2}%` : null,
          ].filter(Boolean).join(' \u00B7 ')
        : 'Not recorded',
    },
    {
      key: 'meds', icon: <MedicalServices fontSize="small" />, label: 'Medications given',
      present: ctx.medications.length > 0,
      preview: ctx.medications.length > 0 ? `${ctx.medications.length} dose entries` : 'None',
    },
    {
      key: 'rounds', icon: <NoteAlt fontSize="small" />, label: 'Ward rounds',
      present: ctx.rounds.length > 0,
      preview: ctx.rounds.length > 0 ? `${ctx.rounds.length} round${ctx.rounds.length === 1 ? '' : 's'} on file` : 'None',
    },
  ]), [dischargeReason, ctx, latestVitals]);

  const canGenerate = !!(ctx.documenting || ctx.other || ctx.history || ctx.complaint);

  const handleGenerate = async () => {
    if (!pid) {
      Swal.fire('No patient', 'Select a patient before generating a note.', 'info');
      return;
    }
    if (!canGenerate) {
      Swal.fire('Not enough context', 'A diagnosis or doctor note is needed before a discharge note can be drafted.', 'info');
      return;
    }

    setGenerating(true);
    try {
      const payload = {
        patientId:    pid,
        patientName:  pname,
        age:          patient?.age,
        gender:       patient?.gender,
        actionDate:   visitDt,
        selectedDate: visitDt,
        startDate:    visitDt,
        endDate:      new Date().toISOString().slice(0, 10),
        diagnosisText:  ctx.documenting,
        proceduresText: ctx.procedure_,
        treatmentPlan:  ctx.other || ctx.admmission_note || ctx.history || '',
        complaint:      ctx.complaint,
        physicalExam:   ctx.exam,
        otherDoc:       ctx.other,
        // Discharge-specific signals + the user-selected reason.
        isDischarge:     true,
        dischargeReason: dischargeReason || '',
        isDetention: false,
        isOPD:       false,
        generationIndex: genIndex,
        excludeNamesFromNotes: true,
      };
      const res = await axios.post(generateNurseNote, payload);
      if (res?.data?.success) {
        const txt = res.data?.data?.nurseNote || '';
        setGenerated(txt);
        setGenIndex(i => i + 1);
      } else {
        throw new Error(res.data?.message || 'AI generation failed');
      }
    } catch (err) {
      console.error('AI discharge note error', err);
      const blocked = err.response?.status === 403 && err.response?.data?.licenseBlocked;
      Swal.fire(
        blocked ? 'AI Disabled' : 'Generation Failed',
        blocked ? 'AI generation is disabled on this license.' : (err.response?.data?.message || err.message || 'Could not generate the note.'),
        'error',
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleUse = () => {
    if (generated && typeof onGenerated === 'function') {
      onGenerated(generated);
    }
    onClose && onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, py: 2,
        background: AI_GRADIENT, color: 'white',
      }}>
        <AutoAwesome />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2 }}>
            AI Discharge Note
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', opacity: 0.9 }}>
            {pname ? `${pname} \u00B7 ID ${pid}` : 'Select a patient first'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2.5, bgcolor: '#f8fafc' }}>
        <Typography sx={{
          fontSize: '0.78rem', fontWeight: 700, color: '#64748b',
          textTransform: 'uppercase', letterSpacing: '0.6px', mb: 1,
          display: 'flex', alignItems: 'center', gap: 1,
        }}>
          Considered Parameters
          {loadingCtx && <CircularProgress size={12} sx={{ color: '#94a3b8' }} />}
        </Typography>

        <Stack spacing={1}>
          {params.map(p => (
            <Paper key={p.key} elevation={0} sx={{
              p: 1.25, borderRadius: 2,
              border: '1px solid #e5e9ef', bgcolor: '#fff',
              display: 'flex', alignItems: 'center', gap: 1.25,
            }}>
              <Box sx={{
                width: 28, height: 28, borderRadius: 1.5,
                bgcolor: p.present ? 'rgba(106,17,203,0.10)' : '#f1f5f9',
                color:   p.present ? '#6a11cb' : '#94a3b8',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                {p.icon}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
                  {p.label}
                </Typography>
                <Typography noWrap sx={{ fontSize: '0.74rem', color: '#64748b', mt: 0.25 }}>
                  {p.preview}
                </Typography>
              </Box>
              {p.present
                ? <CheckCircle sx={{ color: '#16a34a', fontSize: 20 }} />
                : <Cancel sx={{ color: '#cbd5e1', fontSize: 20 }} />}
            </Paper>
          ))}
        </Stack>

        {generated && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ my: 1.5 }}>
              <Chip label="Generated" size="small" sx={{
                bgcolor: 'rgba(106,17,203,0.10)', color: '#6a11cb', fontWeight: 700,
              }} />
            </Divider>
            <Paper elevation={0} sx={{
              p: 1.5, borderRadius: 2, border: '1px solid #e5e9ef', bgcolor: '#fff',
            }}>
              <Typography sx={{ fontSize: '0.9rem', color: '#0f172a', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                {generated}
              </Typography>
            </Paper>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, bgcolor: '#fff' }}>
        {!generated ? (
          <>
            <Button onClick={onClose} disabled={generating} sx={{ color: '#64748b' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={generating || loadingCtx || !canGenerate}
              startIcon={generating ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <AutoAwesome />}
              sx={{
                background: AI_GRADIENT,
                textTransform: 'none', fontWeight: 700, px: 3,
                '&:hover': { filter: 'brightness(1.05)' },
              }}
            >
              {generating ? 'Generating\u2026' : 'Generate Note'}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} sx={{ color: '#64748b' }}>
              Cancel
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={generating}
              startIcon={generating ? <CircularProgress size={16} /> : <Refresh />}
              sx={{ color: '#6a11cb', textTransform: 'none', fontWeight: 700 }}
            >
              Try another
            </Button>
            <Button
              variant="contained"
              onClick={handleUse}
              startIcon={<CheckCircle />}
              sx={{
                background: AI_GRADIENT,
                textTransform: 'none', fontWeight: 700, px: 3,
                '&:hover': { filter: 'brightness(1.05)' },
              }}
            >
              Use This Note
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
